import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, Clock } from 'lucide-react';
import api from '../lib/api';
import { useLanguage } from '../contexts/LanguageContext';
import SEO from '../components/SEO';

const T = {
  en: {
    eyebrow: 'Insights', title: 'Latest articles from ClicksyHub',
    subtitle: 'Case studies, playbooks and perspectives on digital sales for financial institutions.',
    readArticle: 'Read article', minRead: 'min read', featured: 'Featured',
    empty: 'No articles yet.', filters: { all: 'All', 'case-study': 'Case studies', insights: 'Insights', strategy: 'Strategy' },
  },
  es: {
    eyebrow: 'Ideas', title: 'Últimos artículos de ClicksyHub',
    subtitle: 'Casos, playbooks y perspectivas sobre ventas digitales para instituciones financieras.',
    readArticle: 'Leer artículo', minRead: 'min de lectura', featured: 'Destacado',
    empty: 'Todavía no hay artículos.', filters: { all: 'Todos', 'case-study': 'Casos', insights: 'Ideas', strategy: 'Estrategia' },
  },
};

export default function BlogListPage() {
  const { lang } = useLanguage();
  const t = T[lang] || T.en;
  const [articles, setArticles] = useState([]);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/public/articles').then((r) => setArticles(r.data || [])).finally(() => setLoading(false));
  }, []);

  const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString(lang === 'es' ? 'es-ES' : 'en-US', { year: 'numeric', month: 'long', day: 'numeric' }); } catch { return iso; } };
  const tr = (a) => (a.translations?.[lang] || a.translations?.en || {});

  const categories = Array.from(new Set(articles.map(a => a.category).filter(Boolean)));
  const filtered = filter === 'all' ? articles : articles.filter(a => a.category === filter);
  const featured = filtered.find(a => a.featured) || filtered[0];
  const rest = featured ? filtered.filter(a => a.slug !== featured.slug) : filtered;

  return (
    <>
      <SEO title={`${t.title} | ClicksyHub`} description={t.subtitle} />
      <section className="pt-24 pb-12 lg:pt-36 lg:pb-16">
        <div className="container-x">
          <span className="eyebrow">{t.eyebrow}</span>
          <h1 className="heading-display mt-8 text-[44px] sm:text-[64px] lg:text-[96px] max-w-4xl">{t.title}</h1>
          <p className="mt-6 text-[16px] text-[#C6C4CF] max-w-2xl">{t.subtitle}</p>
          {categories.length > 0 && (
            <div className="mt-10 flex flex-wrap gap-2">
              {[['all', t.filters.all], ...categories.map(c => [c, t.filters[c] || c])].map(([key, label]) => (
                <button key={key} onClick={() => setFilter(key)} className={`text-[11px] uppercase tracking-[0.22em] px-4 py-2.5 rounded-full transition-colors ${filter === key ? 'bg-[#FAF6EF] text-[#232323]' : 'border border-[#3A332A] text-[#C6C4CF] hover:border-[#F5F137] hover:text-[#FAF6EF]'}`}>{label}</button>
              ))}
            </div>
          )}
        </div>
      </section>

      {loading ? null : filtered.length === 0 ? (
        <section className="py-20 text-center text-[#C6C4CF] container-x">{t.empty}</section>
      ) : (
        <>
          {featured && (
            <section className="pb-16 lg:pb-24 border-t border-[#3A332A]">
              <div className="container-x pt-12">
                <Link to={`/blog/${featured.slug}`} className="group grid grid-cols-1 lg:grid-cols-12 gap-8 items-end">
                  <div className="lg:col-span-7 aspect-[16/9] overflow-hidden bg-[#2A2A2A]">
                    <img src={featured.image || '/images/blog-1.webp'} alt={tr(featured).title} className="w-full h-full object-cover grayscale-[20%] group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700" />
                  </div>
                  <div className="lg:col-span-5">
                    <div className="flex items-center gap-3 text-xs uppercase tracking-[0.22em] text-[#C6C4CF]">
                      <span className="border border-[#C6C4CF] px-2.5 py-1 rounded-full">{t.featured}</span>
                      <span>{fmtDate(featured.date)}</span>
                      <span className="flex items-center gap-1"><Clock size={12} />{featured.reading_minutes} {t.minRead}</span>
                    </div>
                    <h2 className="heading-display text-3xl lg:text-5xl mt-5 group-hover:text-[#F5F137] transition-colors">{tr(featured).title}</h2>
                    <p className="mt-4 text-[#C6C4CF] leading-relaxed">{tr(featured).excerpt}</p>
                    <span className="mt-6 inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-[#FAF6EF]">{t.readArticle} <ArrowUpRight size={14} className="group-hover:rotate-45 transition-transform" /></span>
                  </div>
                </Link>
              </div>
            </section>
          )}
          {rest.length > 0 && (
            <section className="pb-24 border-t border-[#3A332A]">
              <div className="container-x pt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-12">
                {rest.map((a) => (
                  <Link to={`/blog/${a.slug}`} key={a.slug} className="group">
                    <div className="aspect-[4/3] overflow-hidden bg-[#2A2A2A]">
                      <img src={a.image || '/images/blog-2.webp'} alt={tr(a).title} className="w-full h-full object-cover grayscale-[30%] group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700" loading="lazy" />
                    </div>
                    <div className="pt-5">
                      <div className="flex items-center gap-3 text-xs uppercase tracking-[0.22em] text-[#C6C4CF]">
                        <span>{fmtDate(a.date)}</span>
                        <span className="flex items-center gap-1"><Clock size={12} />{a.reading_minutes} {t.minRead}</span>
                      </div>
                      <h3 className="heading-display text-2xl mt-3 group-hover:text-[#F5F137] transition-colors">{tr(a).title}</h3>
                      <p className="mt-3 text-[#C6C4CF] text-[15px] leading-relaxed line-clamp-3">{tr(a).excerpt}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </>
      )}
    </>
  );
}
